import { useMemo } from 'react';
import type { DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faImage, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { AdminSectionHeading } from '../../admin-ui';
import type { UploadedLogo } from '../../hooks';
import type { ThemeSettingsPageStyles } from './themeSettingsPageStyles';
import type { ThemeLogoSectionViewModel } from './useThemeLogoSettings';
import { LOGO_GUIDELINES, LOGO_ACCEPT } from './themeSettingsUtils';

interface LogoDropZoneProps {
  styles: ThemeSettingsPageStyles;
  isActive: boolean;
  dirty: boolean;
  disabled: boolean;
  onClick: () => void;
  onKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
  onDragOver: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDragEnter: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: ReactDragEvent<HTMLDivElement>) => void;
  onDrop: (event: ReactDragEvent<HTMLDivElement>) => void;
  ariaLabel: string;
  ariaDescribedBy?: string | undefined;
}

function LogoDropZone({
  styles,
  isActive,
  dirty,
  disabled,
  onClick,
  onKeyDown,
  onDragOver,
  onDragEnter,
  onDragLeave,
  onDrop,
  ariaLabel,
  ariaDescribedBy,
}: LogoDropZoneProps) {
  const combinedStyle = useMemo(
    () => ({ ...styles.assistantAvatarTile, width: '260px', maxWidth: '100%', ...(dirty ? styles.dirtyControl : {}), ...(isActive ? styles.logoDropZoneActive : {}) }),
    [styles, isActive, dirty]
  );
  return (
    <div
      style={combinedStyle}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
    >
      <div style={styles.assistantUploadButton}>
        <span style={styles.assistantUploadTitle}>Upload Logo</span>
        <span style={styles.assistantUploadSize}>5 MB max</span>
        <span style={styles.assistantUploadIcon} aria-hidden="true">
          <FontAwesomeIcon icon={faPlus} />
        </span>
      </div>
    </div>
  );
}

interface UploadedLogoTileProps {
  logo: UploadedLogo;
  dirty: boolean;
  styles: ThemeSettingsPageStyles;
  onReplace: () => void;
  onDelete: () => void;
  altPrefix: string;
}

function UploadedLogoTile({ logo, dirty, styles, onReplace, onDelete, altPrefix }: UploadedLogoTileProps) {
  return (
    <div style={styles.uploadedLogoGroup}>
      <div style={{ ...styles.uploadedLogoTile, ...(dirty ? styles.dirtyControl : {}) }}>
        <img src={logo.src} alt={`${altPrefix}: ${logo.name}`} style={styles.uploadedLogoImage} />
      </div>
      <div style={styles.uploadedLogoActions}>
        <button type="button" style={styles.uploadedLogoAction('replace')} onClick={onReplace}>
          <span style={styles.uploadedLogoActionIcon} aria-hidden="true"><FontAwesomeIcon icon={faArrowsRotate} style={{ fontSize: 18 }} /></span>
          Replace
        </button>
        <button type="button" style={styles.uploadedLogoAction('delete')} onClick={onDelete}>
          <span style={styles.uploadedLogoActionIcon} aria-hidden="true"><FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 18 }} /></span>
          Delete
        </button>
      </div>
    </div>
  );
}

export type ThemeLogoSectionProps = ThemeLogoSectionViewModel & { styles: ThemeSettingsPageStyles; disabled?: boolean; dirty?: boolean };

export function ThemeLogoSection({
  styles,
  disabled = false,
  dirty = false,
  logo,
  logoError,
  isLogoDragActive,
  logoInputRef,
  logoHandlers,
  logoInputId,
  certLogo,
  certLogoError,
  certificateSaveError,
  isCertLogoDragActive,
  certLogoInputRef,
  certLogoHandlers,
  certLogoInputId,
  useSeparateCertLogo,
  certToggleId,
  onToggleCertChange,
  onRevertCert,
  logoBackgroundColor,
  certBackgroundColor,
}: ThemeLogoSectionProps) {
  const certificateError = certLogoError || certificateSaveError;
  return (
    <section style={styles.card} aria-labelledby="theme-settings-logo">
      <AdminSectionHeading icon={faImage} title="Logo" headingLevel="h2" titleId="theme-settings-logo" />
      <div style={styles.logoBody}>
        <ul style={styles.bulletList}>
          {LOGO_GUIDELINES.map((line) => (
            <li key={line} style={styles.bulletItem}>{line}</li>
          ))}
        </ul>

        {logo ? (
          <div style={styles.uploadedLogoRow}>
            <UploadedLogoTile
              dirty={dirty}
              logo={logo}
              styles={styles}
              onReplace={logoHandlers.triggerFilePicker}
              onDelete={logoHandlers.handleDelete}
              altPrefix="Uploaded logo"
            />

            <div style={styles.uploadedSampleColumn}>
              <div style={styles.uploadedSampleTile(logoBackgroundColor)}>
                <img src={logo.src} alt="" style={styles.uploadedLogoImage} />
              </div>
              <div style={styles.uploadedSampleCaption}>
                <p style={styles.uploadedSampleCaptionTitle}>Bottom of the left panel</p>
                <p style={styles.uploadedSampleCaptionDetail}>You can change this in the color settings below.</p>
              </div>
            </div>

            {!useSeparateCertLogo ? (
              <div style={styles.uploadedSampleColumn}>
                <div style={styles.uploadedSampleTile(certBackgroundColor)}>
                  <img src={logo.src} alt="" style={styles.uploadedLogoImage} />
                </div>
                <div style={styles.uploadedSampleCaption}>
                  <p style={styles.uploadedSampleCaptionTitle}>Completion certificate</p>
                  <label htmlFor={certToggleId} style={styles.certToggleLabel}>
                    <input
                      id={certToggleId}
                      type="checkbox"
                      checked={useSeparateCertLogo}
                      onChange={onToggleCertChange}
                    />
                    Use a different logo
                  </label>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <LogoDropZone
            dirty={dirty}
            disabled={disabled}
            styles={styles}
            isActive={isLogoDragActive}
            onClick={logoHandlers.triggerFilePicker}
            onKeyDown={logoHandlers.handleKeyDown}
            onDragOver={logoHandlers.handleDragOver}
            onDragEnter={logoHandlers.handleDragOver}
            onDragLeave={logoHandlers.handleDragLeave}
            onDrop={logoHandlers.handleDrop}
            ariaLabel="Upload logo. Click or drag a file here."
            ariaDescribedBy={logoError ? `${logoInputId}-error` : undefined}
          />
        )}

        {logoError ? (
          <p id={`${logoInputId}-error`} style={styles.errorMessage} role="alert">
            {logoError}
          </p>
        ) : null}

        <input
          ref={logoInputRef}
          id={logoInputId}
          type="file"
          accept={LOGO_ACCEPT}
          onChange={logoHandlers.handleInputChange}
          style={styles.visuallyHiddenInput}
          aria-hidden="true"
          tabIndex={-1}
        />

        {useSeparateCertLogo ? (
          <div style={styles.certSection}>
            <hr style={styles.certSectionDivider} />
            <h3 style={styles.certSectionHeading}>Completion certificate logo</h3>

            {certLogo ? (
              <div style={styles.uploadedLogoRow}>
                <UploadedLogoTile
                  dirty={dirty}
                  logo={certLogo}
                  styles={styles}
                  onReplace={certLogoHandlers.triggerFilePicker}
                  onDelete={certLogoHandlers.handleDelete}
                  altPrefix="Uploaded certificate logo"
                />

                <div style={styles.uploadedSampleColumn}>
                  <div style={styles.uploadedSampleTile(certBackgroundColor)}>
                    <img src={certLogo.src} alt="" style={styles.uploadedLogoImage} />
                  </div>
                  <div style={styles.uploadedSampleCaption}>
                    <p style={styles.uploadedSampleCaptionTitle}>Completion certificate</p>
                  </div>
                </div>
              </div>
            ) : (
              <LogoDropZone
                dirty={dirty}
                disabled={disabled}
                styles={styles}
                isActive={isCertLogoDragActive}
                onClick={certLogoHandlers.triggerFilePicker}
                onKeyDown={certLogoHandlers.handleKeyDown}
                onDragOver={certLogoHandlers.handleDragOver}
                onDragEnter={certLogoHandlers.handleDragOver}
                onDragLeave={certLogoHandlers.handleDragLeave}
                onDrop={certLogoHandlers.handleDrop}
                ariaLabel="Upload completion certificate logo. Click or drag a file here."
                ariaDescribedBy={certificateError ? `${certLogoInputId}-error` : undefined}
              />
            )}

            {certificateError ? (
              <p id={`${certLogoInputId}-error`} style={styles.errorMessage} role="alert">
                {certificateError}
              </p>
            ) : null}

            <input
              ref={certLogoInputRef}
              id={certLogoInputId}
              type="file"
              accept={LOGO_ACCEPT}
              onChange={certLogoHandlers.handleInputChange}
              style={styles.visuallyHiddenInput}
              aria-hidden="true"
              tabIndex={-1}
            />

            <button type="button" style={styles.revertLink} onClick={onRevertCert}>
              Use the same logo for the completion certificate
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
